import React, { useEffect, useMemo, useState } from "react";
import { History, Loader2, FileText, GitCompare, Home, LogIn, LogOut, ChevronDown, ChevronRight } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function EdlHistorique({ embedded = false }) {
  const [edls, setEdls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [cmp, setCmp] = useState({});
  const [busy, setBusy] = useState("");
  const [fType, setFType] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fSearch, setFSearch] = useState("");

  useEffect(() => { api.get("/edl").then((r) => setEdls(r.data || [])).catch(() => {}).finally(() => setLoading(false)); }, []);

  const filtered = useMemo(() => edls.filter((e) =>
    (!fType || e.type === fType) &&
    (!fStatus || (fStatus === "completed" ? e.status === "completed" : e.status !== "completed")) &&
    (!fSearch || (e.property_name || "").toLowerCase().includes(fSearch.toLowerCase()))
  ), [edls, fType, fStatus, fSearch]);

  const groups = useMemo(() => {
    const g = {};
    filtered.forEach((e) => { const k = e.property_name || "Bien sans nom"; (g[k] = g[k] || []).push(e); });
    Object.values(g).forEach((arr) => arr.sort((a, b) => (b.edl_date || "").localeCompare(a.edl_date || "")));
    return g;
  }, [filtered]);

  const pdf = async (e) => { try { const res = await api.get(`/edl/${e.id}/pdf`, { responseType: "blob" }); const u = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = u; a.download = `edl-${e.type}-${e.property_name || "bien"}.pdf`; a.click(); URL.revokeObjectURL(u); } catch { toast.error("PDF indisponible"); } };
  const compare = async (e) => {
    setBusy(e.id);
    try { const r = await api.post(`/edl/${e.id}/compare`, {}); setCmp((c) => ({ ...c, [e.id]: r.data })); toast.success("Comparatif généré"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Comparatif impossible (créez d'abord l'état des lieux d'entrée)"); } finally { setBusy(""); }
  };

  if (loading) return <div className="flex items-center justify-center py-24 text-brand-green/50"><Loader2 className="w-7 h-7 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="edl-historique-page">
      {!embedded && (
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center"><History className="w-5 h-5" /></div>
          <div>
            <h2 className="font-display text-2xl font-semibold text-brand-greenDeep">Historique par bien</h2>
            <p className="text-sm text-brand-green/55">Tous les états des lieux, avec comparatif entrée/sortie en un clic</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-3xl border border-brand-soft p-4 flex flex-wrap items-end gap-3" data-testid="hist-filters">
        <label className="block"><span className="text-xs text-brand-green/55 mb-1 block">Type</span>
          <select value={fType} onChange={(e) => setFType(e.target.value)} className="px-3 py-2 rounded-xl border border-brand-soft bg-white outline-none focus:border-brand-lime text-sm text-brand-greenDeep w-40" data-testid="hist-filter-type">
            <option value="">Tous</option><option value="entree">Entrée</option><option value="sortie">Sortie</option>
          </select></label>
        <label className="block"><span className="text-xs text-brand-green/55 mb-1 block">Statut</span>
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="px-3 py-2 rounded-xl border border-brand-soft bg-white outline-none focus:border-brand-lime text-sm text-brand-greenDeep w-40" data-testid="hist-filter-status">
            <option value="">Tous</option><option value="completed">Terminé</option><option value="in_progress">En cours</option>
          </select></label>
        <label className="block flex-1 min-w-[200px]"><span className="text-xs text-brand-green/55 mb-1 block">Rechercher un bien</span>
          <input value={fSearch} onChange={(e) => setFSearch(e.target.value)} placeholder="Ex : Appartement Rivoli…" className="px-3 py-2 rounded-xl border border-brand-soft bg-white outline-none focus:border-brand-lime text-sm text-brand-greenDeep w-full" data-testid="hist-filter-search" /></label>
        {(fType || fStatus || fSearch) && <button onClick={() => { setFType(""); setFStatus(""); setFSearch(""); }} className="px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-sm font-semibold" data-testid="hist-filter-reset">Réinitialiser</button>}
        <span className="text-xs text-brand-green/45 ml-auto self-center" data-testid="hist-count">{filtered.length} EDL</span>
      </div>

      {Object.keys(groups).length === 0 && <p className="text-sm text-brand-green/45 py-6" data-testid="hist-empty">Aucun état des lieux ne correspond.</p>}

      <div className="space-y-3">
        {Object.entries(groups).map(([name, arr]) => {
          const isOpen = open[name] !== false;
          const sorties = arr.filter((e) => e.type === "sortie");
          return (
            <div key={name} className="bg-white rounded-3xl border border-brand-soft overflow-hidden" data-testid={`hist-group-${name}`}>
              <button onClick={() => setOpen((o) => ({ ...o, [name]: !isOpen }))} className="w-full flex items-center justify-between px-5 py-4 hover:bg-brand-softer/40">
                <span className="flex items-center gap-2 font-semibold text-brand-greenDeep"><Home className="w-4 h-4 text-brand-green/50" /> {name} <span className="text-xs font-normal text-brand-green/45">· {arr.length} EDL</span></span>
                {isOpen ? <ChevronDown className="w-5 h-5 text-brand-green/40" /> : <ChevronRight className="w-5 h-5 text-brand-green/40" />}
              </button>
              {isOpen && (
                <div className="border-t border-brand-soft divide-y divide-brand-soft">
                  {arr.map((e) => {
                    const sigs = e.signatures || {};
                    const done = ["tenant", "owner", "referent"].filter((p) => sigs[p]).length;
                    return (
                      <div key={e.id} className="px-5 py-3" data-testid={`hist-edl-${e.id}`}>
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <span className="flex items-center gap-2 text-sm text-brand-greenDeep">
                            {e.type === "sortie" ? <LogOut className="w-4 h-4 text-amber-600" /> : <LogIn className="w-4 h-4 text-emerald-600" />}
                            <b className="capitalize">{e.type === "sortie" ? "Sortie" : "Entrée"}</b> · {e.edl_date}
                            <span className="text-xs text-brand-green/45">· {done}/3 signés · {e.status === "completed" ? "Terminé" : "En cours"}</span>
                          </span>
                          <div className="flex items-center gap-2">
                            <button onClick={() => pdf(e)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-brand-soft text-brand-greenDeep text-xs font-semibold" data-testid={`hist-pdf-${e.id}`}><FileText className="w-3.5 h-3.5" /> PDF</button>
                            {e.type === "sortie" && <button onClick={() => compare(e)} disabled={busy === e.id} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-brand-greenDeep text-white text-xs font-semibold disabled:opacity-60" data-testid={`hist-compare-${e.id}`}>{busy === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GitCompare className="w-3.5 h-3.5" />} Comparatif</button>}
                          </div>
                        </div>
                        {(cmp[e.id] || e.comparison) && (
                          <div className="mt-2 bg-brand-softer rounded-xl p-3" data-testid={`hist-comparison-${e.id}`}>
                            <p className="text-xs text-brand-green/75">{(cmp[e.id] || e.comparison).summary}</p>
                            <div className="mt-1 space-y-1">
                              {((cmp[e.id] || e.comparison).differences || []).map((df, i) => (
                                <div key={i} className="text-[11px] text-brand-green/70 border-l-2 border-brand-lime pl-2"><b>{df.room}</b> — {df.change} <span className="text-brand-green/45">({df.severity} · {df.responsibility})</span></div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {sorties.length === 0 && <div className="px-5 py-2 text-[11px] text-brand-green/40">Astuce : le comparatif est disponible depuis un état des lieux de sortie.</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
